// /src/components/donate/DonatePanelMemberView.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { splitIntroParagraphs } from '../../pages/private/admin/PrivateDocuments/donatePanel/donatePanelUtils';
import { formatTotalLabel } from '../../pages/private/admin/PrivateDocuments/donatePanel/donatePanelCostUtils';
import { fetchDonatorPerksPublic } from '../../pages/private/admin/PrivateDocuments/donatorPerks/donatorPerksApi';
import {
  DonateCostHexTotalIcon,
  DonateCostStarIcon,
  resolveCostRowIcon,
} from './donatePanelCostIcons';
import DonateMonthlyFundingBar from './DonateMonthlyFundingBar';

export default function DonatePanelMemberView({ panel, className = '' }) {
  const introText = panel?.introText || '';
  const footerText = panel?.footerText || '';
  const bannerUrl = panel?.bannerMedia?.url || null;
  const costRows = Array.isArray(panel?.costRows) ? panel.costRows : [];
  const currentMonthDonationEur = panel?.fundingStats?.currentMonthDonationEur ?? 0;
  const currentYearDonationEur = panel?.fundingStats?.currentYearDonationEur ?? 0;
  const paragraphs = splitIntroParagraphs(introText);
  const yearlyTotal = useMemo(() => formatTotalLabel(costRows), [costRows]);
  const [perksIntroText, setPerksIntroText] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadPerksIntro = async () => {
      try {
        const data = await fetchDonatorPerksPublic();
        if (!cancelled && data?.ok) {
          setPerksIntroText(data?.data?.introText || '');
        }
      } catch (_) {
        if (!cancelled) setPerksIntroText('');
      }
    };
    loadPerksIntro();
    return () => {
      cancelled = true;
    };
  }, []);

  const perksIntroParagraphs = useMemo(
    () => splitIntroParagraphs(perksIntroText),
    [perksIntroText],
  );

  const rootClassName = ['donate-panel-member', className].filter(Boolean).join(' ');

  const introBlock = paragraphs.length > 0 ? (
    <div className="donate-panel-member__intro">
      {paragraphs.map((paragraph, index) => (
        <p key={`intro-p-${index}`} className="donate-panel-member__intro-p">
          {paragraph}
        </p>
      ))}
    </div>
  ) : null;

  const perksIntroBlock = perksIntroParagraphs.length > 0 ? (
    <div
      className="kofi-donation-link__intro donate-panel-member__perks-intro"
      data-resource="public.donation-panel"
      data-action="read"
      data-route="/api/public/donator-perks"
      data-description="Donator perks intro"
    >
      {perksIntroParagraphs.map((paragraph, index) => (
        <p key={`kofi-intro-${index}`} className="kofi-donation-link__intro-p">
          {paragraph}
        </p>
      ))}
    </div>
  ) : null;

  return (
    <div className={rootClassName} data-description="Donation panel member view">
      {bannerUrl ? (
        <div className="donate-panel-member__banner">
          <img src={bannerUrl} alt="" className="donate-panel-member__banner-img" />
          {introBlock}
        </div>
      ) : (
        introBlock
      )}

      {perksIntroBlock}

      {costRows.length > 0 ? (
        <section className="donate-panel-member__costs" aria-label="Annual operating costs">
          <DonateMonthlyFundingBar
            costRows={costRows}
            currentMonthDonationEur={currentMonthDonationEur}
            currentYearDonationEur={currentYearDonationEur}
          />

          <h4 className="donate-panel-member__section-title">
            <span className="donate-panel-member__section-lines" aria-hidden="true">
              <span />
              <span />
            </span>
            Annual operating costs
          </h4>

          <div className="donate-panel-member__table-shell">
            <table className="donate-panel-member__table">
              <thead>
                <tr>
                  <th scope="col">Cost center</th>
                  <th scope="col">Purpose</th>
                  <th scope="col">Yearly cost</th>
                </tr>
              </thead>
              <tbody>
                {costRows.map((row) => {
                  const Icon = resolveCostRowIcon(row.category);
                  return (
                    <tr key={row.id ?? `${row.category}-${row.sortOrder}`}>
                      <td>
                        <span className="donate-panel-member__cost-center">
                          <Icon />
                          <span>{row.category}</span>
                        </span>
                      </td>
                      <td>{row.description}</td>
                      <td className="donate-panel-member__amount">{row.amountLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="donate-panel-member__total">
            <DonateCostHexTotalIcon />
            <span className="donate-panel-member__total-label">Estimated yearly cost</span>
            <span className="donate-panel-member__total-value">
              <strong>{yearlyTotal}</strong>
              <span className="donate-panel-member__total-suffix"> / year</span>
            </span>
          </div>
        </section>
      ) : null}

      {footerText ? (
        <p className="donate-panel-member__footer">
          <DonateCostStarIcon />
          <span>{footerText}</span>
        </p>
      ) : null}
    </div>
  );
}
